import type { FC } from 'react';
import { Button, Flex, Input, Modal, Typography } from 'antd';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useSynonymsEditDialog } from './hooks/useSynonymsEditDialog';

const { Text } = Typography;

export type SynonymsEditDialogProps = {
  open: boolean;
  title: string;
  initialSynonyms: string[];
  onApply: (synonyms: string[]) => void | Promise<void>;
  onClose: () => void;
};

export const SynonymsEditDialog: FC<SynonymsEditDialogProps> = ({
  open,
  title,
  initialSynonyms,
  onApply,
  onClose,
}) => {
  const { t } = useTranslation('componentSchema');
  const { drafts, saving, handleChange, handleAdd, handleRemove, handleOk } =
    useSynonymsEditDialog({
      open,
      initialSynonyms,
      onApply,
      onClose,
    });

  return (
    <Modal
      title={t('synonymsDialog.title', { name: title })}
      open={open}
      onCancel={onClose}
      onOk={() => void handleOk()}
      confirmLoading={saving}
      okText={t('synonymsDialog.save')}
      cancelText={t('synonymsDialog.cancel')}
      destroyOnHidden
      width={520}
    >
      <Flex vertical gap={12}>
        <Text type="secondary">{t('synonymsDialog.hint')}</Text>
        {drafts.map((value, index) => (
          <Flex key={`synonym-${index}`} gap={8} align="center">
            <Input
              value={value}
              placeholder={t('synonymsDialog.placeholder')}
              onChange={(event) => handleChange(index, event.target.value)}
              onPressEnter={() => void handleOk()}
            />
            <Button
              type="text"
              danger
              aria-label={t('synonymsDialog.remove')}
              icon={<IconTrash size={16} />}
              onClick={() => handleRemove(index)}
            />
          </Flex>
        ))}
        <Button
          type="dashed"
          icon={<IconPlus size={16} />}
          onClick={handleAdd}
          style={{ alignSelf: 'flex-start' }}
        >
          {t('synonymsDialog.add')}
        </Button>
      </Flex>
    </Modal>
  );
};
